import React from "react";

import { Formik } from "formik";
import type { FormikHelpers } from "formik";
import { get } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { toast } from "react-toastify";
import { push } from "store/actions/router";
import { userConstants } from "actions/types";
import { ScrollToFieldError } from "utils/form";
import { Card, Col, Form, Row } from "react-bootstrap";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import { createSupportRequest } from "actions/userActions";

import { createLoadingSelector, createErrorSelector } from "store/selectors";
import { accountTypesToEnglishMapping } from "statics/accountTypes";

import Alert from "components/Alert";
import Button from "components/Button";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";

import type { RootState } from "types/state";

import * as yup from "yup";

const schema = yup.object({
  subject: yup
    .string()
    .label("Subject")
    .required()
    .min(2, "Must be at least two characters.")
    .max(200, "Must be less than 200 characters."),
  comment: yup
    .string()
    .label("comment")
    .required()
    .min(2, "Must be at least two characters.")
    .max(1000, "Must be less than 1000 characters."),
});

interface SupportRequestFormValues {
  subject: string;
  comment: string;
}

const actions = [userConstants.CREATE_REQUEST];

const loadingSelector = createLoadingSelector(actions);
// @ts-expect-error - PRESERVED QUIRK: `actions` is already a string[], so
// `[actions]` passes a nested string[][] to createErrorSelector (which expects
// string | string[]). Matches the original JS; do not "fix" the wrapping.
const errorSelector = createErrorSelector([actions]);

const mapStateToProps = (state: RootState) => {
  // profile carries arbitrary backend keys via an index signature, so
  // firstName/lastName/email are `unknown`; cast the lookup at the boundary.
  // The `{}` default is preserved to guard a missing profile slice.
  const { firstName, lastName, email } = get(state, "user.profile", {}) as {
    firstName?: string;
    lastName?: string;
    email: string;
  };

  const accounts = state.accounts.iraAccounts;

  // userState slice is typed `object`; `.state` is a backend key cast here.
  const userState = (state.user.userState as { state?: string }).state;

  return {
    email,
    name: `${firstName} ${lastName}`,
    userState,
    error: errorSelector(state),
    isSubmitting: loadingSelector(state),
    accounts,
  };
};

const mapDispatchToProps = {
  push,
  createSupportRequest,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface ZendeskSupportRequestState {
  showRolloverAccountTypeSelection: boolean;
  showCloseRolloverAccount: boolean;
  showDisbursementFlow: boolean;
}

class ZendeskSupportRequest extends React.Component<
  Props,
  ZendeskSupportRequestState
> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showRolloverAccountTypeSelection: false,
      showCloseRolloverAccount: false,
      showDisbursementFlow: false,
    };
  }

  _submitRequest = (
    vals: SupportRequestFormValues,
    { resetForm }: FormikHelpers<SupportRequestFormValues>
  ) => {
    const requestParams = {
      comment: vals.comment,
      subject: vals.subject,
      name: this.props.name,
      email: this.props.email,
    };

    void this.props
      .createSupportRequest(
        this.props.client as ApolloClient<object>,
        requestParams
      )
      .then(() => {
        if (!this.props.error) {
          toast.success("Support request submitted. You'll get an email.");
          resetForm();
        }
      });
  };

  _supportTicket = () => {
    return (
      <div className="transactions-mega-container">
        <section>
          <article>
            <div>
              <Card>
                <Card.Title style={{ padding: 20, margin: 0 }}>
                  <IconHeader
                    variant="cardHeader"
                    headerText="How can we help you?"
                  />
                  <IconSubheader
                    subheader="Let us know how we can help and we will get back to you via
                email as soon as we can."
                  />
                </Card.Title>

                <Card.Body>
                  <Formik<SupportRequestFormValues>
                    validateOnChange={false}
                    validationSchema={schema}
                    onSubmit={this._submitRequest}
                    enableReinitialize={true}
                    initialValues={{ subject: "", comment: "" }}
                  >
                    {({
                      handleSubmit,
                      handleChange,
                      values,
                      touched,
                      errors,
                    }) => (
                      <Form noValidate onSubmit={handleSubmit}>
                        <ScrollToFieldError />

                        {this.props.accounts.map((account) => (
                          <Row
                            className="mb-3"
                            // PRESERVED QUIRK: IraAccount has no `key` field, so
                            // this reads undefined at runtime. The cast keeps the
                            // exact runtime read; faithful to the original JS.
                            key={(account as { key?: React.Key }).key}
                          >
                            <Form.Group as={Col} controlId="formGroupComment">
                              <Form.Label>
                                {
                                  accountTypesToEnglishMapping[
                                    account.accountType as keyof typeof accountTypesToEnglishMapping
                                  ]
                                }
                                : {account.accountNumber}
                              </Form.Label>
                            </Form.Group>
                          </Row>
                        ))}

                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            sm={6}
                            controlId="formGroupSubject"
                          >
                            <Form.Label>Subject</Form.Label>
                            <Form.Control
                              name="subject"
                              value={values.subject}
                              onChange={handleChange}
                              isInvalid={!!errors.subject}
                              isValid={touched.subject && !errors.subject}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.subject}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formGroupComment">
                            <Form.Label>Message</Form.Label>

                            <Form.Control
                              name="comment"
                              as="textarea"
                              placeholder="Please include your account number in the request and any other information you think will be helpful."
                              value={values.comment}
                              onChange={handleChange}
                              isInvalid={!!errors.comment}
                              style={{ minHeight: "150px" }}
                              isValid={touched.comment && !errors.comment}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.comment}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>

                        {this.props.error && (
                          <Alert type="error" msg={this.props.error} />
                        )}
                        <div className="submit-row btn-row">
                          <Button
                            name="cancel"
                            btnLabel={"Cancel"}
                            color={"cancel"}
                            onClick={() =>
                              this.props.push("/dashboard/support")
                            }
                          />
                          <Button
                            name="submit"
                            btnLabel="Submit"
                            withArrow={true}
                            loading={this.props.isSubmitting}
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>
            </div>
          </article>
        </section>
      </div>
    );
  };

  render() {
    return this._supportTicket();
  }
}

export default connector(withApollo<Props>(ZendeskSupportRequest));
